import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import isUndefined from 'lodash/fp/isUndefined';
import { useEffect, useState } from 'react';
import { Flags } from 'react-feature-flags';
import { useIntl } from 'react-intl';
import { DEFAULT_VEHICLE_PROFILE } from '../../common/constants';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import {
    useGetVehicleProfileListQuery,
    useLazyGetVehicleProfileDetailsQuery,
    type VehicleProfileListItem,
} from '../../store/facade/facadeApiV3';
import {
    selectDefaultEvProfile,
    selectIsEV,
    selectIsProfileSubmited,
    selectProfileType,
    selectProfileTypeField,
} from '../../store/vehicleProfile/vehicleProfileSelectors';
import {
    changeDefaultEvProfile,
    changeProfileDetails,
    changeVehicleType,
    resetProfileDetails,
} from '../../store/vehicleProfile/vehicleProfileSlice';
import { VehicleType } from '../../store/vehicleProfile/vehicleProfileTypes';
import { commonHeaderClassName } from '../SettingsOptions/SettingsOptions';
import { VehicleBatterySelect } from './VehicleBatterySelect';
import {
    CargoWeightField,
    DeviceIdField,
    EmptyWeightField,
    HazardClassificationField,
    HeightField,
    LengthField,
    OutdoorTemperatureField,
    PlugTypeField,
    ProfileField,
    TotalWeightField,
    TrailerAxlesField,
    TrailerField,
    TunnelCategoryField,
    VehicleAxlesField,
    VehicleTypeField,
    WidthField,
    customVehicleOption,
    vehicleTypeOptionLabels,
} from './VehicleFields';

export const VehicleOptions = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();

    const { data: profilesList, isError: profilesListError } = useGetVehicleProfileListQuery();
    const [fetchProfile, { data: profileDetails }] = useLazyGetVehicleProfileDetailsQuery();

    const isEV = useAppSelector(selectIsEV).value;
    const vehicleType = useAppSelector(selectProfileType);
    const profileTypeField = useAppSelector(selectProfileTypeField);
    const isProfileSubmited = useAppSelector(selectIsProfileSubmited);
    const defaultEvProfile = useAppSelector(selectDefaultEvProfile);

    const [evOptions, setEvOptions] = useState<SelectOption[]>([]);
    const [iceOptions, setIceOptions] = useState<SelectOption[]>(vehicleTypeOptionLabels);

    const handleProfileChange = (selectedOption: string) => {
        const selectedProfile = selectedOption as VehicleType;
        dispatch(changeVehicleType(selectedProfile));

        if (selectedOption === VehicleType.CUSTOM_EV || selectedOption === VehicleType.CUSTOM_ICE) {
            dispatch(resetProfileDetails());
        } else if (isEV) {
            fetchProfile({ id: selectedProfile }, true);
        }
    };

    useEffect(() => {
        if (profilesListError && profileTypeField.isEditable) {
            dispatch(changeVehicleType(isEV ? VehicleType.CUSTOM_EV : VehicleType.CUSTOM_ICE));
        }
        if (profilesList) {
            const fetchedEvOptions = profilesList.map(profile => ({
                id: profile.id,
                label: intl.formatMessage({ id: `intl-msg:smartRoute.vehicle.profile.${profile.profileName}` }),
            }));
            setEvOptions([...fetchedEvOptions, customVehicleOption]);

            const profileIndex = profilesList.findIndex(
                (profile: VehicleProfileListItem) => profile.profileName === DEFAULT_VEHICLE_PROFILE
            );
            const defaultProfileId = profilesList[profileIndex].id;
            if (isUndefined(vehicleType) && !isUndefined(defaultProfileId) && profileTypeField.isEditable) {
                dispatch(changeDefaultEvProfile(defaultProfileId));
                dispatch(changeVehicleType(defaultProfileId));
                fetchProfile({ id: defaultProfileId }, true);
            }
        }
    }, [profilesList]);

    useEffect(() => {
        if (!profileTypeField.isEditable) {
            if (isEV && vehicleType && !profileDetails) {
                fetchProfile({ id: vehicleType }, true);
            }
            if (isEV && profileDetails && profileDetails.id === vehicleType) {
                dispatch(changeProfileDetails(profileDetails));
            }
            return;
        }

        const isCustomProfile = vehicleType === VehicleType.CUSTOM_EV || vehicleType === VehicleType.CUSTOM_ICE;
        const isToResetProfile = !isProfileSubmited && isCustomProfile;

        if (isToResetProfile) {
            dispatch(resetProfileDetails());
        }
        if (!isProfileSubmited && isEV && profileDetails && !isCustomProfile) {
            dispatch(changeVehicleType(profileDetails.id));
            dispatch(changeProfileDetails(profileDetails));
        }

        if (isEV && isUndefined(profileDetails) && !isCustomProfile && defaultEvProfile) {
            fetchProfile({ id: defaultEvProfile }, true);
        }

        if (!isEV && !isProfileSubmited) {
            const isIceVehicleType = vehicleTypeOptionLabels
                .map(option => option.id)
                .includes(vehicleType as VehicleType);
            setIceOptions(vehicleTypeOptionLabels);
            dispatch(resetProfileDetails());
            dispatch(changeVehicleType(isIceVehicleType ? vehicleType : iceOptions[0].id));
        }
    }, [vehicleType, isEV, profileDetails]);

    return (
        <div className={`VehicleOptions ${commonHeaderClassName} margin-bottom-15`} data-testid='vehicle-options'>
            {/* Editable fields section */}
            <div data-testid='vehicle-editable-fields'>
                {/* Profile */}
                <div data-testid='vehicle-type'>
                    <ProfileField
                        evOptions={evOptions.filter(option => option.id !== VehicleType.CUSTOM_EV)}
                        iceOptions={iceOptions.filter(option => option.id !== VehicleType.CUSTOM_ICE)}
                        handleProfileChange={handleProfileChange}
                        disabled={!profileTypeField.isEditable}
                    />
                </div>

                {/* Empty Weight + Total Weight */}
                <div className='display-flex align-items-end gap-10' data-testid='vehicle-weights'>
                    <EmptyWeightField />
                    <TotalWeightField />
                </div>

                {/* Cargo Weight */}
                <div className='display-flex align-items-end gap-10' data-testid='vehicle-cargo-weight'>
                    <CargoWeightField />
                </div>

                {/* Tunnel Category + Hazard Class (Dangerous Goods) */}
                <div className='display-flex align-items-end gap-10'>
                    <TunnelCategoryField />
                    <HazardClassificationField />
                </div>

                {/* Battery Capacity + Outdoor Temperature */}
                {isEV && (
                    <div className='display-flex align-items-end gap-10' data-testid='vehicle-battery-temperature'>
                        <VehicleBatterySelect />
                        <Flags authorizedFlags={['vehicleOutdoorTemperature']}>
                            <OutdoorTemperatureField />
                        </Flags>
                    </div>
                )}

                {/* Plug Type */}
                <div className='display-flex align-items-end gap-10' data-testid='vehicle-plug-type'>
                    {isEV && <PlugTypeField />}
                </div>
            </div>

            {/* Separator between editable and read-only fields */}
            <span className='separator' style={{ display: 'block', width: '50%', margin: '0 auto' }}>
                <hr />
            </span>

            {/* Read-only profile fields section */}
            <div data-testid='vehicle-profile-fields'>
                {/* Height + Width + Length */}
                <div className='display-flex align-items-end gap-10' data-testid='vehicle-dimensions'>
                    <HeightField />
                    <WidthField />
                    <LengthField />
                </div>

                {/* Vehicle Type + Trailer */}
                <div className='display-flex align-items-end gap-10' data-testid='vehicle-type-trailer'>
                    <VehicleTypeField />
                    <TrailerField />
                </div>

                {/* Vehicle Axles + Trailer Axles */}
                <div className='display-flex align-items-end gap-10' data-testid='vehicle-axles'>
                    <VehicleAxlesField />
                    <TrailerAxlesField />
                </div>

                {/* Device ID */}
                <Flags authorizedFlags={['deviceId']}>
                    <DeviceIdField />
                </Flags>
            </div>
        </div>
    );
};
